import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Eventos, Calendario y Galería. Módulo 8.
 *
 * ## Lo que cambió en el servidor, decidido con quien encargó el trabajo
 *
 *   · `GET /api/events/` acepta `cuando=proximos|pasados`. Sin él responde
 *     como siempre. Hacía falta: pedía los 30 primeros desde el MÁS ANTIGUO,
 *     así que con 30 eventos celebrados los próximos dejaban de aparecer.
 *   · `GET /api/events/upcoming` filtra desde hoy. El panel del Inicio enseñaba
 *     eventos de junio como «próximos».
 *   · `include_inactive` se concede con `eventos:manage`, el mismo permiso que
 *     reactivar, y no con `is_staff_user`.
 *   · La búsqueda global devuelve `eventos/<id>`.
 *
 * El resto de endpoints no se toca. Lo vigila `test_eventos_pantalla.py`.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

/** Los tres valores del Enum `event_modality_enum`. */
export type Modalidad = 'presencial' | 'virtual' | 'hibrido';

/** Lo que devuelve `_event_dict` (`events.py`). `event_date` es `timestamptz`
 *  y llega con su desfase: `"2026-12-01T10:00:00-05:00"`. */
export interface Evento {
  id: number;
  title: string;
  description?: string | null;
  event_date: string;
  location?: string | null;
  modality: Modalidad | string;
  max_capacity?: number | null;
  registered_count: number;
  is_registered: boolean;
  cover_url?: string | null;
  is_pinned_on_join: boolean;
  is_active: boolean;
}

/** Una foto de la galería (`get_event`). `caption` existe en la tabla pero
 *  ningún endpoint lo escribe —0 de 6 en la base el 2026-09-17—, así que la
 *  pantalla no lo pinta. */
export interface FotoDeEvento {
  id: number;
  order: number;
  url: string;
  thumb_url?: string | null;
}

export interface EventoConGaleria extends Evento {
  gallery: FotoDeEvento[];
}

export interface Inscrito {
  user_id: number;
  full_name: string;
  email?: string | null;
  registered_at?: string | null;
  attended: boolean;
}

interface ElementoBase {
  id: number;
  title: string;
  day: number;
}

/** Cada cosa que pinta el calendario (`calendar.py:get_calendar`). Los `id`
 *  NO comparten espacio: el de un evento es de `events` y el de un cumpleaños
 *  es de `users`. */
export type ElementoDelCalendario =
  | (ElementoBase & { kind: 'event'; time: string; modality: string; location?: string | null })
  | (ElementoBase & { kind: 'special'; icon: string; color: string; recurring: boolean; description?: string | null })
  | (ElementoBase & { kind: 'birthday'; icon: string; area: string; position: string })
  | (ElementoBase & { kind: 'anniversary'; icon: string; years: number; area: string; position: string })
  | (ElementoBase & { kind: 'matrimonio' | 'nacimiento_hijo'; icon: string; etiqueta: string; area: string; position: string });

export type TipoDeElemento = ElementoDelCalendario['kind'];

export interface MesDelCalendario {
  year: number;
  month: number;
  items: ElementoDelCalendario[];
  /** Las celebraciones solo llegan si además se tiene el módulo `celebraciones`:
   *  lo decide el servidor, y sin él el calendario sale sin ellas y sin error. */
  incluye_celebraciones: boolean;
}

export interface FechaEspecial {
  id: number;
  name: string;
  description?: string | null;
  month: number;
  day: number;
  year?: number | null;
  icon?: string | null;
  color?: string | null;
  recurring: boolean;
}

/* ── La hora ───────────────────────────────────────────────────────────── */

/**
 * Un instante del servidor, en el formato de un `<input type="datetime-local">`:
 * `2026-12-01T10:00`, **en la hora del navegador**.
 *
 * El vanilla hacía `new Date(e.event_date).toISOString().slice(0, 16)`, que es
 * la hora UTC. Medido el 2026-09-17 en Bogotá sobre un evento de las 10:00: el
 * formulario de edición enseñaba 15:00, y «Guardar cambios» sin tocar nada lo
 * movía a las 3 de la tarde. Cada vez que alguien lo editaba, cinco horas más.
 */
export function aHoraLocal(instante: string): string {
  const d = new Date(instante);
  if (Number.isNaN(d.getTime())) return '';
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
}

/** Lo contrario: el valor de un `datetime-local` —hora local, sin desfase— al
 *  instante que se guarda. `new Date('2026-12-01T10:00')` ya lo lee como local. */
export function desdeHoraLocal(valor: string): string {
  return new Date(valor).toISOString();
}

/** ¿Ya pasó? Con el mismo criterio que el servidor: un evento de HOY todavía no,
 *  aunque haya empezado, porque no tiene hora de fin. */
export function yaPaso(instante: string): boolean {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return new Date(instante) < hoy;
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  todo: ['eventos'] as const,
  lista: (cuando: 'proximos' | 'pasados', inactivos: boolean, limite: number) =>
    ['eventos', 'lista', cuando, inactivos, limite] as const,
  detalle: (id: number) => ['eventos', 'detalle', id] as const,
  inscritos: (id: number) => ['eventos', 'inscritos', id] as const,
  mes: (anio: number, mes: number) => ['eventos', 'calendario', anio, mes] as const,
  especiales: ['eventos', 'especiales'] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

export function useEventos(cuando: 'proximos' | 'pasados', inactivos: boolean, limite: number) {
  return useQuery({
    queryKey: claves.lista(cuando, inactivos, limite),
    queryFn: () =>
      api.get<Evento[]>('/events/', {
        cuando,
        limit: limite,
        ...(inactivos ? { include_inactive: 'true' } : {}),
      }),
    // Al pedir la tanda siguiente de pasados cambia la clave: sin esto, las
    // tarjetas que ya se veían se sustituirían por el esqueleto hasta que llegue.
    placeholderData: keepPreviousData,
  });
}

export function useEvento(id: number | null) {
  return useQuery({
    queryKey: claves.detalle(id ?? 0),
    queryFn: () => api.get<EventoConGaleria>(`/events/${id}`),
    enabled: id != null,
  });
}

export function useInscritos(id: number, habilitado: boolean) {
  return useQuery({
    queryKey: claves.inscritos(id),
    queryFn: () => api.get<Inscrito[]>(`/events/${id}/registrations`),
    enabled: habilitado,
  });
}

export function useMes(anio: number, mes: number) {
  return useQuery({
    queryKey: claves.mes(anio, mes),
    queryFn: () => api.get<MesDelCalendario>('/calendar/', { year: anio, month: mes }),
    // Al cambiar de mes, la rejilla anterior se queda hasta que llega la nueva:
    // un esqueleto a cada clic haría saltar la página.
    placeholderData: keepPreviousData,
  });
}

export function useFechasEspeciales(habilitado: boolean) {
  return useQuery({
    queryKey: claves.especiales,
    queryFn: () => api.get<FechaEspecial[]>('/calendar/special-dates'),
    enabled: habilitado,
  });
}

/* ── Escrituras ────────────────────────────────────────────────────────── */

/** Todo lo que se escribe invalida el módulo entero: la lista, el detalle, el
 *  calendario y la galería leen los mismos eventos, y son pocas peticiones. */
function useInvalidarEventos() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: claves.todo });
}

export interface CamposDeEvento {
  title: string;
  description: string | null;
  event_date: string;
  location: string | null;
  modality: Modalidad;
  max_capacity: number | null;
  is_pinned_on_join: boolean;
}

/**
 * Crear o editar, y subir la portada si la hay.
 *
 * **La portada no puede duplicar el evento.** El vanilla creaba el evento y
 * después subía la portada dentro del mismo `try`: si la subida fallaba, el
 * formulario seguía abierto con los mismos datos, y volver a pulsar «Crear
 * evento» hacía otro `POST`. Medido el 2026-09-17: dos eventos iguales.
 *
 * Aquí el evento se da por guardado en cuanto el servidor lo confirma. Si
 * después falla la portada, se devuelve el error **sin lanzarlo**, para que la
 * pantalla cierre el formulario y diga que el evento existe pero sin portada.
 */
export function useGuardarEvento() {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: async ({ id, campos, portada }: { id: number | null; campos: CamposDeEvento; portada: File | null }) => {
      let idFinal = id;
      if (idFinal == null) idFinal = (await api.post<{ id: number }>('/events/', campos)).id;
      else await api.put(`/events/${idFinal}`, campos);

      let errorDePortada: unknown = null;
      if (portada) {
        try {
          const datos = new FormData();
          datos.append('file', portada);
          await api.upload(`/events/${idFinal}/cover-image`, datos);
        } catch (e) {
          errorDePortada = e;
        }
      }
      return { id: idFinal, errorDePortada };
    },
    onSettled: invalidar,
  });
}

export function useDesactivar() {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (id: number) => api.post(`/events/${id}/deactivate`, null),
    onSuccess: invalidar,
  });
}

export function useReactivar() {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (id: number) => api.post(`/events/${id}/reactivate`, null),
    onSuccess: invalidar,
  });
}

export function useInscribirse() {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (id: number) => api.post<{ message: string }>(`/events/${id}/register`, null),
    onSuccess: invalidar,
  });
}

export function useCancelarInscripcion() {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (id: number) => api.del(`/events/${id}/register`),
    onSuccess: invalidar,
  });
}

/** Idempotente en el servidor: si ya estaba publicado responde 200 con
 *  `ya_existia`, y la pantalla lo dice en vez de celebrar otra publicación. */
export function usePublicarEnMuro() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ message: string; post_id: number; ya_existia: boolean }>(`/events/${id}/publish-feed`, null),
  });
}

/** Hasta 20 fotos por envío: el servidor corta en silencio las que sobran
 *  (`files[:20]`), así que la pantalla no deja elegir más. */
export const MAXIMO_DE_FOTOS = 20;
export const TOPE_MB_POR_FOTO = 10;

export function useSubirFotos(idEvento: number) {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (fotos: File[]) => {
      const datos = new FormData();
      fotos.forEach((f) => datos.append('files', f));
      return api.upload<{ uploaded: number }>(`/events/${idEvento}/gallery`, datos);
    },
    onSuccess: invalidar,
  });
}

export function useBorrarFoto(idEvento: number) {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (idFoto: number) => api.del(`/events/${idEvento}/gallery/${idFoto}`),
    onSuccess: invalidar,
  });
}

export function useLimpiarDuplicados(idEvento: number) {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: () => api.post<{ message: string; removed: number }>(`/events/${idEvento}/gallery/dedupe`, null),
    onSuccess: invalidar,
  });
}

export function usePublicarGaleria(idEvento: number) {
  return useMutation({
    mutationFn: () =>
      api.post<{ message: string; post_id: number; ya_existia: boolean }>(`/events/${idEvento}/gallery/publish`, null),
  });
}

export interface CamposDeFechaEspecial {
  name: string;
  description: string | null;
  month: number;
  day: number;
  year: number | null;
  icon: string;
  color: string;
}

export function useCrearFechaEspecial() {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (campos: CamposDeFechaEspecial) => api.post<{ id: number }>('/calendar/special-dates', campos),
    onSuccess: invalidar,
  });
}

/** Baja lógica: la fila se queda con `is_active = false` y un registro de
 *  auditoría. */
export function useBorrarFechaEspecial() {
  const invalidar = useInvalidarEventos();
  return useMutation({
    mutationFn: (id: number) => api.del(`/calendar/special-dates/${id}`),
    onSuccess: invalidar,
  });
}
